import { useState } from 'react'
import { ChevronDown, ChevronUp, ChevronRight, SlidersHorizontal, Info } from 'lucide-react'
import { labelCls, inputCls } from '../styles'
import { DEFAULT_RISK_LEVELS, DEFAULT_CONFIDENCE_LEVELS, DEFAULT_RISK_FACTORS } from '../data'
import type { ConfidenceLevelBand, RiskFactorConfig, RiskLevelBand } from '../types'
import { applyIdSuggestions, useAiSeed } from '../aiConfigure'
import type { AiIdSuggestion, AiSeed } from '../aiConfigure'

function SubSection({ title, description, children, defaultOpen = false }: { title: string; description: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="rounded-[10px] overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-[#ffffff]"
        style={{ background: '#f8fafc' }}
      >
        {open ? <ChevronDown size={12} className="text-[#64748b] shrink-0" /> : <ChevronRight size={12} className="text-[#64748b] shrink-0" />}
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold text-[#1e293b]">{title}</p>
          <p className="text-[10px] text-[#64748b] mt-0.5 leading-snug">{description}</p>
        </div>
      </button>
      {open && <div className="p-3 space-y-2" style={{ borderTop: '1px solid #e5e7eb' }}>{children}</div>}
    </div>
  )
}

interface RiskProfileConfigPanelProps {
  /** AI Configure from Scope — applied once the rep accepts the suggestion. */
  aiSeed?: AiSeed<AiIdSuggestion[]> | null
}

export function RiskProfileConfigPanel({ aiSeed }: RiskProfileConfigPanelProps = {}) {
  const [open, setOpen] = useState(false)
  const [riskLevels, setRiskLevels] = useState<RiskLevelBand[]>(DEFAULT_RISK_LEVELS)
  const [confidenceLevels, setConfidenceLevels] = useState<ConfidenceLevelBand[]>(DEFAULT_CONFIDENCE_LEVELS)
  const [riskFactors, setRiskFactors] = useState<RiskFactorConfig[]>(DEFAULT_RISK_FACTORS)

  useAiSeed(aiSeed, (suggestions) => setRiskFactors((prev) => applyIdSuggestions(prev, suggestions)))

  function updateRiskLevel(id: string, patch: Partial<RiskLevelBand>) {
    setRiskLevels((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)))
  }

  function updateConfidenceLevel(id: string, patch: Partial<ConfidenceLevelBand>) {
    setConfidenceLevels((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)))
  }

  function toggleRiskFactor(id: string) {
    setRiskFactors((prev) => prev.map((f) => (f.id === id ? { ...f, enabled: !f.enabled } : f)))
  }

  function updateRiskFactorWeight(id: string, weight: number) {
    setRiskFactors((prev) => prev.map((f) => (f.id === id ? { ...f, weight } : f)))
  }

  return (
    <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-[#ffffff]"
        style={{ background: '#f8fafc' }}
      >
        <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">
          <SlidersHorizontal size={12} className="text-[#4f46e5]" />
          Risk Profile Configuration
        </span>
        {open ? <ChevronUp size={12} className="text-[#64748b]" /> : <ChevronDown size={12} className="text-[#64748b]" />}
      </button>

      {open && (
        <div className="p-4 space-y-3">
          <p className="text-[10px] text-[#475569] flex items-start gap-1.5">
            <Info size={11} className="shrink-0 mt-0.5" />
            Modelled on Operations Center&apos;s Risk Profiles configuration — the score bands, confidence adjustments and risk factors that combine to produce this customer&apos;s overall risk level.
          </p>

          <SubSection title="Risk Levels" description="Score ranges that map to LOW / MEDIUM / HIGH / UNACCEPTABLE" defaultOpen>
            <div className="space-y-1.5">
              {riskLevels.map((band) => (
                <div key={band.id} className="flex items-center gap-2 px-3 py-2 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-wider w-28 text-center shrink-0" style={{ background: `${band.color}20`, color: band.color }}>
                    {band.label}
                  </span>
                  <span className="text-[10px] text-[#64748b] shrink-0">Range:</span>
                  <input
                    type="number"
                    className={inputCls}
                    value={band.minScore}
                    onChange={(e) => updateRiskLevel(band.id, { minScore: Number(e.target.value) || 0 })}
                  />
                  <span className="text-[10px] text-[#475569] shrink-0">–</span>
                  <input
                    type="number"
                    className={inputCls}
                    value={band.maxScore ?? ''}
                    placeholder="∞"
                    onChange={(e) => updateRiskLevel(band.id, { maxScore: e.target.value === '' ? null : Number(e.target.value) })}
                  />
                </div>
              ))}
            </div>
          </SubSection>

          <SubSection title="Confidence Levels" description="How confident the match is, and the score adjustment it applies">
            <div className="space-y-1.5">
              {confidenceLevels.map((band) => (
                <div key={band.id} className="flex items-center gap-2 px-3 py-2 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                  <span className="text-[10px] font-mono font-semibold text-[#475569] w-20 shrink-0">{band.label}</span>
                  <span className="text-[10px] text-[#64748b] shrink-0">Adjustment</span>
                  <input
                    type="number"
                    className={inputCls}
                    value={band.adjustment}
                    onChange={(e) => updateConfidenceLevel(band.id, { adjustment: Number(e.target.value) || 0 })}
                  />
                  <span className="text-[10px] text-[#64748b] shrink-0">Range</span>
                  <input
                    type="number"
                    className={inputCls}
                    value={band.minScore ?? ''}
                    placeholder="—"
                    onChange={(e) => updateConfidenceLevel(band.id, { minScore: e.target.value === '' ? null : Number(e.target.value) })}
                  />
                  <span className="text-[10px] text-[#475569] shrink-0">–</span>
                  <input
                    type="number"
                    className={inputCls}
                    value={band.maxScore ?? ''}
                    placeholder="—"
                    onChange={(e) => updateConfidenceLevel(band.id, { maxScore: e.target.value === '' ? null : Number(e.target.value) })}
                  />
                </div>
              ))}
            </div>
          </SubSection>

          <SubSection title="Risk Factors" description={`${riskFactors.filter((f) => f.enabled).length}/${riskFactors.length} enabled — each contributes its weight toward the overall risk score`}>
            <div>
              <label className={labelCls}>General</label>
              <div className="space-y-1.5">
                {riskFactors.map((factor) => (
                  <div
                    key={factor.id}
                    className="flex items-center gap-3 px-3 py-2 rounded-[9px]"
                    style={factor.enabled
                      ? { background: '#f8fafc', border: '1px solid #e5e7eb' }
                      : { background: '#f8fafc', border: '1px solid #e5e7eb', opacity: 0.55 }
                    }
                  >
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                      <input type="checkbox" checked={factor.enabled} onChange={() => toggleRiskFactor(factor.id)} className="sr-only peer" />
                      <div className="w-8 h-[18px] rounded-full transition-colors" style={{ background: factor.enabled ? '#6366f1' : '#e5e7eb' }} />
                      <div className="absolute left-[3px] top-[3px] w-3 h-3 rounded-full bg-white transition-transform" style={{ transform: factor.enabled ? 'translateX(14px)' : 'translateX(0)' }} />
                    </label>
                    <span className="text-[11px] text-[#1e293b] flex-1 min-w-0 truncate">{factor.label}</span>
                    <span className="text-[9px] text-[#475569] shrink-0">weight</span>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      className={inputCls}
                      style={{ width: 56 }}
                      value={factor.weight}
                      onChange={(e) => updateRiskFactorWeight(factor.id, Number(e.target.value) || 0)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </SubSection>
        </div>
      )}
    </div>
  )
}
